import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import withFilters from 'coreOld/components/withFilters';
import { IFiltersProps } from 'coreOld/types';
import { getListPayload } from 'coreOld/utils';
import { useGetImportPartsList, useImportPartsActions } from 'partsOld/hooks';
import { IManufacturer } from 'partsOld/types';

import ChooseManufacturerTableView from './ChooseManufacturerTableView';
import { style } from './chooseManufacturerTableStyle';
import { ManufacturerValidationStatus, SelectOptions } from './enums';

interface IProps extends IFiltersProps {
  importId: number;
  isModalOpen: boolean;
  onClose: () => void;
  setIsUnknownManufactures: Dispatch<SetStateAction<boolean>>;
  refetchColumns;
}

const getPayload = (filters, importId) => {
  const defaultFilters = {
    importId: null,
    manufacturerValidationStatus: 'unknown',
    omit: '0',
    number: '',
    description: '',
    possibleManufacturerName: '',
  };

  return getListPayload(
    {
      ...defaultFilters,
      ...filters,
      importId,
    },
    {
      pageSize: 100,
    }
  );
};

const getFilters = (filters, importId) => getPayload(filters, importId);

//TODO -> Add selection all option (#85zrxt5pj)

const ChooseManufacturerTable = ({
  filters,
  importId,
  isModalOpen,
  onClose,
  refetchColumns,
  setIsUnknownManufactures,
}: IProps) => {
  const [isSaved, setIsSaved] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const { records, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, refetch } =
    useGetImportPartsList(getFilters(filters, importId), isModalOpen);

  useEffect(() => {
    if (records.length === 0 && isSaved) {
      setIsUnknownManufactures(false);

      onClose();
      refetchColumns();
    }
  }, [records]);

  const { partsSaveBulk } = useImportPartsActions();

  const dataBySelectOption = (type, selectedItems, data) => {
    const partImports = [];

    const { filters: defaultFilters } = getFilters(filters, importId);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { description, number, possibleManufacturerName, ...rest } = defaultFilters;

    switch (type) {
      case SelectOptions.SELECT_ALL:
        return {
          partImport: [data],
          filtered: 1,
          filters: rest,
        };
      case SelectOptions.DISPLAYED:
        selectedItems.map((item) => partImports.push({ id: item.id, ...data }));

        return {
          partImports,
        };
      case SelectOptions.FILTERED:
        return {
          partImport: [data],
          filtered: 1,
          filters: defaultFilters,
        };
      default:
        selectedItems.map((item) => partImports.push({ id: item.id, ...data }));

        return {
          partImports,
        };
    }
  };

  const onAssignClose = () => setIsAssignOpen(false);

  const onDoNotImport = async (selectedItems) => {
    const data = dataBySelectOption(undefined, selectedItems, { omit: 1 });

    await partsSaveBulk(data);

    refetch();
    setIsSaved(true);
  };

  const onAssignManufacturer = async (selectedItems, manufacturer: IManufacturer) => {
    const data = dataBySelectOption(SelectOptions.SELECT_ALL, selectedItems, {
      manufacturerId: manufacturer.id,
      manufacturerValidationStatus: ManufacturerValidationStatus.VALID,
    });

    await partsSaveBulk(data);

    refetch();
    setIsSaved(true);
  };

  const selectOptionsConfig = (selectedItems) => [
    {
      section: [
        [
          {
            label: 'ASSIGN MANUFACTURER',
            onClick: () => setIsAssignOpen(true),
            btnProps: {
              style,
            },
          },
        ],
      ],
    },
    {
      section: [
        [
          {
            label: 'DO NOT IMPORT',
            onClick: () => onDoNotImport(selectedItems),
            btnProps: {
              style,
            },
          },
        ],
      ],
    },
    {
      section: [
        [
          {
            label: 'DO NOT ASSIGN MANUFACTURER',
            disabled: true,
            btnProps: {
              style,
            },
          },
        ],
      ],
    },
  ];

  return (
    <ChooseManufacturerTableView
      {...{
        isFetchingNextPage,
        isLoading,
        records,
        hasNextPage,
        fetchNextPage,
        selectOptionsConfig,
        selectionType: 'part',
        onAssignClose,
        isAssignOpen,
        filters: getFilters(filters, importId),
        onAssignManufacturer,
      }}
    />
  );
};

export default withFilters(ChooseManufacturerTable);
